import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Chip,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  SupervisorAccount as AdminsIcon,
  CheckCircle as ActiveIcon,
  Shield as SuperAdminIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as DeactivateIcon,
  CheckCircleOutline as ActivateIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import apiClient from '../services/api/apiClient';
import adminService from '../services/api/adminService';

interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: 'super_admin' | 'admin' | 'moderator';
  is_active: boolean;
  last_login: string;
  created_at: string;
}

const EMPTY_FORM = { first_name: '', last_name: '', email: '', password: '', role: 'moderator' as AdminUser['role'] };

function getRoleColor(role: AdminUser['role']): 'primary' | 'success' | 'warning' {
  if (role === 'super_admin') return 'primary';
  if (role === 'admin')       return 'success';
  return 'warning';
}

function getRoleLabel(role: AdminUser['role']): string {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin')       return 'Admin';
  return 'Moderator';
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

const AVATAR_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

export default function AdminUsersPage() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM });
  const [createLoading, setCreateLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState<Partial<typeof EMPTY_FORM>>({});

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', role: 'moderator' as AdminUser['role'], reset_password: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Deactivate confirmation
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const res = await apiClient.get('/admin/users/', { params: { type: 'admin' } });
        const raw = res.data?.data?.users ?? res.data?.data ?? [];
        // Map backend shape → AdminUser shape
        const mapped: AdminUser[] = raw.map((u: any) => ({
          id: u.id,
          first_name: u.first_name ?? u.name?.split(' ')[0] ?? '',
          last_name: u.last_name ?? u.name?.split(' ').slice(1).join(' ') ?? '',
          email: u.email,
          role: u.is_superuser || u.role === 'super_admin' ? 'super_admin' : u.role === 'moderator' ? 'moderator' : 'admin',
          is_active: u.is_active ?? u.status === 'active',
          last_login: u.last_login ?? '',
          created_at: u.created_at ?? '',
        }));
        setAdmins(mapped);
      } catch (err: any) {
        setActionError(err.response?.data?.message || 'Failed to load admin users');
        setAdmins([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAdmins();
  }, []);

  const totalAdmins  = admins.length;
  const activeAdmins = admins.filter(a => a.is_active).length;
  const superAdmins  = admins.filter(a => a.role === 'super_admin').length;

  // --- Create ---
  const validateCreate = () => {
    const errs: Partial<typeof EMPTY_FORM> = {};
    if (!createForm.first_name.trim()) errs.first_name = 'Required';
    if (!createForm.last_name.trim())  errs.last_name  = 'Required';
    if (!createForm.email.trim() || !/\S+@\S+\.\S+/.test(createForm.email)) errs.email = 'Valid email required';
    if (createForm.password.length < 8) errs.password = 'Min 8 characters';
    return errs;
  };

  const handleCreateSubmit = async () => {
    const errs = validateCreate();
    if (Object.keys(errs).length > 0) { setCreateErrors(errs); return; }
    setCreateLoading(true);
    try {
      const res = await adminService.createAdminUser({
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        email: createForm.email.trim().toLowerCase(),
        password: createForm.password,
        role: createForm.role === 'super_admin' ? 'admin' : createForm.role,
        phone_number: '00000000000', // required by backend — admin can update later
      });
      const created = res;
      const newAdmin: AdminUser = {
        id: created?.user_id ?? String(Date.now()),
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        email: createForm.email.trim().toLowerCase(),
        role: createForm.role,
        is_active: true,
        last_login: '',
        created_at: new Date().toISOString(),
      };
      setAdmins(prev => [newAdmin, ...prev]);
      setCreateOpen(false);
      setCreateForm({ ...EMPTY_FORM });
      setCreateErrors({});
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to create admin user');
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Edit ---
  const handleOpenEdit = (admin: AdminUser) => {
    setEditTarget(admin);
    setEditForm({ first_name: admin.first_name, last_name: admin.last_name, email: admin.email, role: admin.role, reset_password: '' });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editTarget) return;
    setEditLoading(true);
    try {
      await apiClient.patch(`/admin/users/${editTarget.id}/`, {
        action: 'update',
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        reset_password: editForm.reset_password || undefined,
      });
      setAdmins(prev => prev.map(a => a.id === editTarget.id
        ? { ...a, first_name: editForm.first_name, last_name: editForm.last_name, email: editForm.email, role: editForm.role }
        : a));
      setEditOpen(false);
      setEditTarget(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to update admin');
    } finally {
      setEditLoading(false);
    }
  };

  // --- Deactivate / Activate ---
  const handleOpenDeactivate = (admin: AdminUser) => {
    setDeactivateTarget(admin);
    setDeactivateOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivateLoading(true);
    try {
      const action = deactivateTarget.is_active ? 'suspend' : 'unsuspend';
      await apiClient.patch(`/admin/users/${deactivateTarget.id}/`, { action });
      setAdmins(prev => prev.map(a => a.id === deactivateTarget.id ? { ...a, is_active: !a.is_active } : a));
      setDeactivateOpen(false);
      setDeactivateTarget(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setDeactivateLoading(false);
    }
  };

  // --- Delete ---
  const handleOpenDelete = (admin: AdminUser) => {
    setDeleteTarget(admin);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/admin/users/${deleteTarget.id}/`);
      setAdmins(prev => prev.filter(a => a.id !== deleteTarget.id));
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setActionError(err.response?.data?.message || 'Failed to delete admin');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      width: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const admin = params.row as AdminUser;
        const idx = admins.findIndex(a => a.id === admin.id) % AVATAR_COLORS.length;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700,
                bgcolor: AVATAR_COLORS[idx],
              }}
            >
              {getInitials(admin.first_name, admin.last_name)}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.2 }}>
                {admin.first_name} {admin.last_name}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 210,
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getRoleLabel(params.value)}
          color={getRoleColor(params.value)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: params.value ? 'text.primary' : 'text.disabled' }}>
          {params.value ? formatDate(params.value) : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const admin = params.row as AdminUser;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={e => { e.stopPropagation(); handleOpenEdit(admin); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={admin.is_active ? 'Deactivate' : 'Activate'}>
              <IconButton
                size="small"
                color={admin.is_active ? 'warning' : 'success'}
                onClick={e => { e.stopPropagation(); handleOpenDeactivate(admin); }}
              >
                {admin.is_active ? <DeactivateIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); handleOpenDelete(admin); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const paginatedRows = admins.slice(
    paginationModel.page * paginationModel.pageSize,
    (paginationModel.page + 1) * paginationModel.pageSize,
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Admin Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage administrator accounts and their access levels
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setCreateOpen(true); setCreateForm({ ...EMPTY_FORM }); setCreateErrors({}); }}
        >
          Create Admin
        </Button>
      </Box>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Total Admins"
            value={totalAdmins}
            subtitle="All admin accounts"
            icon={<AdminsIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Active Admins"
            value={activeAdmins}
            subtitle="Currently active"
            icon={<ActiveIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            title="Super Admins"
            value={superAdmins}
            subtitle="Full platform access"
            icon={<SuperAdminIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                '& .MuiDataGrid-root': { border: 'none', borderRadius: 0 },
                '& .MuiDataGrid-cell': { borderColor: 'divider' },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: 'background.default',
                  borderColor: 'divider',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
                '& .MuiDataGrid-footerContainer': { borderColor: 'divider' },
              }}
            >
              <DataGrid
                rows={paginatedRows}
                columns={columns}
                rowCount={admins.length}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25]}
                paginationMode="server"
                disableRowSelectionOnClick
                rowHeight={60}
                sx={{ minHeight: 350 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Create Admin Dialog ─────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Admin User</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth label="First Name" size="small"
                value={createForm.first_name}
                onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))}
                error={!!createErrors.first_name}
                helperText={createErrors.first_name}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Last Name" size="small"
                value={createForm.last_name}
                onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))}
                error={!!createErrors.last_name}
                helperText={createErrors.last_name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Email Address" size="small" type="email"
                value={createForm.email}
                onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                error={!!createErrors.email}
                helperText={createErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Password" size="small" type="password"
                value={createForm.password}
                onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                error={!!createErrors.password}
                helperText={createErrors.password || 'Minimum 8 characters'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select label="Role" size="small"
                value={createForm.role}
                onChange={e => setCreateForm(f => ({ ...f, role: e.target.value as AdminUser['role'] }))}
              >
                <MenuItem value="super_admin">Super Admin — Full access</MenuItem>
                <MenuItem value="admin">Admin — Standard access</MenuItem>
                <MenuItem value="moderator">Moderator — Limited access</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {createLoading ? 'Creating...' : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Admin Dialog ───────────────────────────── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Admin User</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth label="First Name" size="small"
                value={editForm.first_name}
                onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Last Name" size="small"
                value={editForm.last_name}
                onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Email Address" size="small" type="email"
                value={editForm.email}
                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Reset Password (optional)" size="small" type="password"
                placeholder="Leave blank to keep current password"
                value={editForm.reset_password}
                onChange={e => setEditForm(f => ({ ...f, reset_password: e.target.value }))}
                helperText="Only fill this to reset the password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select label="Role" size="small"
                value={editForm.role}
                onChange={e => setEditForm(f => ({ ...f, role: e.target.value as AdminUser['role'] }))}
              >
                <MenuItem value="super_admin">Super Admin — Full access</MenuItem>
                <MenuItem value="admin">Admin — Standard access</MenuItem>
                <MenuItem value="moderator">Moderator — Limited access</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Deactivate / Activate Confirmation Dialog ───── */}
      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {deactivateTarget?.is_active ? 'Deactivate' : 'Activate'} Admin
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deactivateTarget?.is_active
              ? <>Deactivating <strong>{deactivateTarget?.first_name} {deactivateTarget?.last_name}</strong> will revoke their access to the admin panel.</>
              : <>Reactivating <strong>{deactivateTarget?.first_name} {deactivateTarget?.last_name}</strong> will restore their admin panel access.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeactivateOpen(false)} disabled={deactivateLoading}>Cancel</Button>
          <Button
            variant="contained"
            color={deactivateTarget?.is_active ? 'warning' : 'success'}
            onClick={handleConfirmDeactivate}
            disabled={deactivateLoading}
            startIcon={deactivateLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deactivateLoading ? 'Updating...' : deactivateTarget?.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ──────────────────── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Admin User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete{' '}
            <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
